import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { sdkClientQuery, sdkClientQueryVariables } from '../generated/sdkClientQuery';
import { sdkClientFieldsFragment } from '../models/SdkClient';
import { mainApiConfig } from '../../../configure/mainApiConfig';

/**
 * Get a SdkClient from the store.
 * @param id
 * @param options
 */
export function useSdkClient(id: string | undefined | null, options: AsyncLoadOptions = {}): AsyncLoadResult<sdkClientQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<sdkClientQuery, sdkClientQueryVariables>(
        gql`
        query sdkClientQuery ($id: ID!) {
            model: sdkClient(id: $id) {
                ...sdkClientFields
            }
        }

        ${sdkClientFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
